
import NewObjectButton from "./NewObjectButton";
import DraggableObjectListItem from "./DraggableObjectListItem";
import { useModelStore, useUserStore } from "src/store";
import { ObjectDto, ObjectInfos } from "@dto/objects";
import { TW_ObjectListContainer } from "@utils/twClasses";
import { updateStorageSelection } from "@utils/localStorage";
import { FullRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";
import { useState } from "react";
import { put } from "@utils/utils";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { toastError } from "@utils/toasts";
import { translate } from "src/i18n/i18n";
import { useTranslation } from "react-i18next";


interface ObjectListProps {
  onMobileClose?: () => void;
}

function ObjectList({ onMobileClose }: ObjectListProps) {

  const objectInfos = useModelStore(state => state.objectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const updateObjectInfos = useModelStore(state => state.updateObjectInfos);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const selectedView = useModelStore(state => state.selectedView);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const userRole = useModelStore(state => state.userRole);
  const { t } = useTranslation();
  const user = useUserStore(state => state.user);
  
  // Sort objects by order to ensure correct display order
  const sortedObjects = objectInfos ? [...objectInfos].sort((a, b) => (a.object.order || 0) - (b.object.order || 0)) : [];
  const [draggedObjectKey, setDraggedObjectKey] = useState<string | null>(null);
  const [dropLineState, setDropLineState] = useState<{
    objectKey: string;
    position: 'top' | 'bottom';
  } | null>(null);

  const newObject = (object: ObjectDto) => {
    const newObjectInfo = { object: object, fields: [], relations: [], views: [] };
    const newObjectInfos = [...(objectInfos || []), newObjectInfo];
    updateObjectInfos(newObjectInfos, newObjectInfo, null, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newObjectInfo.object?._key, null, null);
  }

  const handleObjectOrderUpdate = (updatedObjectInfos: ObjectInfos) => {
    const newAllObjectInfos = objectInfos.map((o: ObjectInfos) => {
      if (o.object._key === updatedObjectInfos.object._key) {
        o = updatedObjectInfos;
      }
      return o;
    });
    if (selectedObjectInfos?.object._key === updatedObjectInfos.object._key) {
      selectedObjectInfos.object.order = updatedObjectInfos.object.order;
    }
    updateObjectInfos(newAllObjectInfos, selectedObjectInfos, selectedView, null, sharedObjectInfos);
  };

  const handleDragStart = (objectKey: string) => {
    setDraggedObjectKey(objectKey);
  };

  const handleDragEnd = () => {
    setDraggedObjectKey(null);
    setDropLineState(null);
  };

  const handleDragOver = (objectKey: string, position: 'top' | 'bottom' | null) => {
    if (position && draggedObjectKey && draggedObjectKey !== objectKey) {
      setDropLineState({ objectKey, position });
    } else {
      setDropLineState(null);
    }
  };

  const handleObjectDrop = (draggedObjectKey: string, targetObjectKey: string, position: 'top' | 'bottom' | null) => {
    const draggedObject = sortedObjects.find(o => o.object._key === draggedObjectKey);
    const targetObject = sortedObjects.find(o => o.object._key === targetObjectKey);

    if (!draggedObject || !targetObject || !position) return;

    const targetIndex = sortedObjects.findIndex(o => o.object._key === targetObjectKey);
    let newOrder: number;

    if (position === 'top') {
      // Dropping above target object
      if (targetIndex === 0) {
        // Dropping at the beginning
        newOrder = (sortedObjects[0].object.order || 0) - DEFAULT_ORDER_INCREMENT;
      } else {
        // Dropping between previous and target
        const prevOrder = sortedObjects[targetIndex - 1].object.order || 0;
        const targetOrder = sortedObjects[targetIndex].object.order || 0;
        newOrder = (prevOrder + targetOrder) / 2;
      }
    } else {
      // Dropping below target object
      if (targetIndex === sortedObjects.length - 1) {
        // Dropping at the end
        newOrder = (sortedObjects[sortedObjects.length - 1].object.order || 0) + DEFAULT_ORDER_INCREMENT;
      } else {
        // Dropping between target and next
        const targetOrder = sortedObjects[targetIndex].object.order || 0;
        const nextOrder = sortedObjects[targetIndex + 1].object.order || 0;
        newOrder = (targetOrder + nextOrder) / 2;
      }
    }

    // Update the object order via API
    const updatedObject = { ...draggedObject.object, order: newOrder };
    const updatedObjectInfos = { ...draggedObject, object: updatedObject };

    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    put<ObjectDto>(`${BASE_URL}/model/workspaces/${workspace._key}/objects`, updatedObject)
      .then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
          }
        } else {
          handleObjectOrderUpdate(updatedObjectInfos);
        }
      });
  };

  const hasFullObjectRight = hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess);
  const hasViewFullRight = hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess);
  return (
    <>
      <div key={"object-list-" + workspace?._key} className={TW_ObjectListContainer}>
        {sortedObjects.map((objectInfos) => {
          const hasCurrentFullObjectRight = hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess, objectInfos.object._key);
          // If the user doesn't have view right, we only show the object if it has views
          if (hasCurrentFullObjectRight || hasViewFullRight || (objectInfos.views && objectInfos.views.length > 0)) {
            return (
              <DraggableObjectListItem
                key={objectInfos.object._key}
                objectInfos={objectInfos}
                onMobileClose={onMobileClose}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleObjectDrop}
                showDropLineTop={dropLineState?.objectKey === objectInfos.object._key && dropLineState?.position === 'top'}
                showDropLineBottom={dropLineState?.objectKey === objectInfos.object._key && dropLineState?.position === 'bottom'}
              />
            )
          }
        })}
        {hasFullObjectRight && <NewObjectButton onNewObject={newObject} />}
      </div>
    </>
  )
};

export default ObjectList;